import { observer } from 'mobx-react-lite';
import * as React from 'react';
import {
    addDays,
    addMinutes,
    formatUserTime,
    getTimestamp,
    getTimezoneOffset,
    isAfter,
    OwaDate,
    owaDate,
    parseUserTime,
    startOfDay,
    startOfHour,
    differenceInMinutes,
    isSameDay,
    isEqual,
} from 'owa-datetime';
import { ComboBox, IComboBox, IComboBoxOption, IComboBoxStyles } from '@fluentui/react/lib';
import { getPalette } from 'owa-theme';
import { getUserConfiguration } from 'owa-session-store';
import { IButtonStyles } from '@fluentui/react/lib/Button';
import { IComboBoxOptionStyles } from '@fluentui/react/lib/ComboBox';
import { TimePickerProps } from './TimePicker.Props';
import formatEndTime from '../utils/formatEndTime';
import { getClosestTime } from '../utils/getClosestTime';
import { mergeStyles } from '@uifabric/merge-styles';
import {
    getDefaultShouldShortenEventDuration,
    getDefaultShouldShortenEventDurationStartLate,
    getDefaultShouldShortenEventDurationEndEarly,
    getDefaultEndLongEventsBy,
    getDefaultEndShortEventsBy,
} from 'owa-calendar-cloud-settings';
import { getConfiguredEventEndTime } from 'owa-calendar-cloud-settings-utils';
import { isFeatureEnabled } from 'owa-feature-flags';

let classNames = require('classnames');
let styles = require('./TimePicker.scss');

export interface TimePickerOption extends IComboBoxOption {
    date: OwaDate /* Date value associated with the option (displayed date) */;
    isSpeedyMeetingOption?: boolean /* True if the time option is adjusted based on the speedy meetings settings */;
}

const chevronStyles: Partial<IButtonStyles> = {
    root: styles.chevronRoot,
    icon: styles.chevronIcon,
};

/**
 * Component uses fabric ComboBox to select time.
 */
export default observer(function TimePicker(props: TimePickerProps) {
    React.useEffect(() => {
        if (props.setFocusOnMount) {
            comboBox.current.current.focus();
        }
    }, []);
    const comboBox = React.useRef(React.createRef<IComboBox>());
    const comboBoxOptionStyle = (): Partial<IComboBoxOptionStyles> => {
        let themeSymbols = getPalette();
        const { timePickerColorScheme } = props;
        const rowHoverColor = timePickerColorScheme
            ? timePickerColorScheme.rowHoverColor
            : themeSymbols.neutralLighter;
        const selectedRowColor = timePickerColorScheme
            ? timePickerColorScheme.selectedRowColor
            : themeSymbols.themeLighter;
        return {
            root: {
                whiteSpace: 'nowrap',
                backgroundColor: themeSymbols.white + ' !important', // Override Fabric theme color !important
            },
            rootHovered: {
                backgroundColor: rowHoverColor + ' !important', // Override Fabric theme color !important
            },
            rootChecked: {
                backgroundColor: selectedRowColor + ' !important', // Override Fabric theme color !important
            },
        };
    };
    /**
     * onChanged: Callback issued when either:
     * 1) the selected option changes
     * 2) a manually edited value is submitted.
     *    In this case there may not be a matched option if allowFreeform is also true
     *   (and hence only value would be true, the other parameter would be null in this case)
     * @param {TimePickerOption} option option
     * @param {number} index option index
     * @param {string} value input value
     */
    const onChanged = (
        event: React.FormEvent<IComboBox>,
        option: IComboBoxOption & {
            date: OwaDate;
            isSpeedyMeetingOption?: boolean;
        },
        index: number,
        value: string
    ) => {
        const newDate = option ? option.date : value ? getDateFromRawText(value) : null;
        if (newDate) {
            props.onChanged(newDate, !!value, option?.isSpeedyMeetingOption);
        }
    };
    const getDateFromRawText = (value: string): OwaDate | null => {
        const { selectedTime } = props;
        const newDate = parseUserTime(value, selectedTime);
        return newDate;
    };
    /**
     * Return JSX element of each option in the callout list
     * @param {TimePickerOption} option option to be rendered in callout list
     */
    const onRenderOption = ({ date, text }: TimePickerOption): JSX.Element => {
        let {
            startTime,
            durationClassName,
            showDuration = true,
            shouldAdjustStartTimeEndTime,
            minutesInterval,
        } = props;
        const { HourIncrement } = getUserConfiguration().UserOptions;
        const showApproxDuration =
            shouldAdjustStartTimeEndTime && getDefaultShouldShortenEventDuration();
        const timeInterval = minutesInterval || HourIncrement || 30;
        return (
            <div
                className={classNames('ms-ComboBox-optionText', styles.timePickerOptionText)}
                id={getTimePickerOptionId(date)}>
                {text}
                {showDuration && startTime && (
                    <span className={durationClassName}>
                        {' ' + formatEndTime(date, startTime, timeInterval, showApproxDuration)}
                    </span>
                )}
            </div>
        );
    };
    /**
     * Create the TimeOption objects backing each context menu item in time picker
     */
    const createTimeOptions = (): TimePickerOption[] => {
        const { HourIncrement } = getUserConfiguration().UserOptions;
        const {
            minutesInterval,
            startTime,
            selectedTime,
            eventStartTime,
            eventEndTime,
            shouldAdjustStartTimeEndTime,
            snapToInterval,
        } = props;
        // Have a fallback value in case the UserOptions object is undefined.
        const timeInterval = minutesInterval || HourIncrement || 30;

        // When startTime is specified we must make sure we move it to the same time zone
        // as the selectedTime. Cases like the Calendar Event compose allow start and
        // end times to be in different time zones and we don't want to mix them.
        const startTimeInCorrectTz = startTime
            ? owaDate(selectedTime.tz, startTime)
            : isSpeedyAndRegularTimesFeatureEnabled()
            ? startOfDay(selectedTime)
            : updateStartTimeWhenStartEventsLate(startOfDay(selectedTime), eventEndTime);
        const end = addDays(startOfDay(startTimeInCorrectTz), 1);
        let shouldUpdateEndTime =
            shouldAdjustStartTimeEndTime &&
            getDefaultShouldShortenEventDurationStartLate() &&
            props.isEndTimePicker &&
            !!startTime &&
            !!selectedTime &&
            isSameDay(startTime, selectedTime);
        let time = startTimeInCorrectTz;
        const dates: OwaDate[] = [];
        while (isAfter(end, time)) {
            dates.push(time);

            // For Start Events Late, we want to add the original and the adjusted time options to the start time picker
            // For example: 1:00, 1:05, 1:30, 1:35 etc.
            if (isSpeedyAndRegularTimesFeatureEnabled()) {
                const updatedStartTime = updateStartTimeWhenStartEventsLate(time, eventEndTime);

                if (isAfter(end, updatedStartTime) && !isEqual(time, updatedStartTime)) {
                    dates.push(updatedStartTime);
                }
            }

            let newTime: OwaDate;
            if (time === startTimeInCorrectTz && (snapToInterval || shouldUpdateEndTime)) {
                // We need to manually adjust the time to be aligned with the interval
                newTime = startOfHour(time);
                while (isAfter(time, newTime) || isEqual(time, newTime)) {
                    newTime = addMinutes(newTime, timeInterval);
                }
            } else {
                newTime = addMinutes(time, timeInterval);
            }
            // When DST ends the clock winds backwards and we end up with duplicated hours (ex, Nov 4 2018 1am PST).
            // Although this is a valid date, we don't want to show it, so we skip it.
            // TZ_BUG: 38589: fpintos: why would we not show the 2nd 1am? Without it, the user can't create
            // events with certain durations. For example, if I want a 2h event starting at 12a I can't do it.
            const offsetDiff = getTimezoneOffset(newTime) - getTimezoneOffset(time);
            if (offsetDiff > 0) {
                newTime = addMinutes(newTime, offsetDiff);
            }
            time = newTime;
        }
        const updatedDates = updateDates(eventStartTime, dates);

        // If speedy meetings are enabled, time picker will include both speedy times and regular times.
        return updatedDates.map((updatedDate, index) => ({
            date: updatedDate,
            text: formatUserTime(updatedDate),
            key: getTimestamp(updatedDate),
            isSpeedyMeetingOption: shouldIncludeBothSpeedyAndRegularTimes()
                ? isSpeedyMeetingIndex(index) // Check this when changing the time options.
                : false,
        }));
    };

    /**
     * Checks if the time picker should include both speedy times and non-speedy/regular times.
     * i.e. if the speedy meetings - start late is enabled, the start time picker will include both kinds of times;
     * if the speedy meetings - end early is enabled, the end time picker will include both kinds of times;
     */
    const shouldIncludeBothSpeedyAndRegularTimes = (): boolean => {
        if (!isSpeedyAndRegularTimesFeatureEnabled()) {
            return false;
        }

        const shouldEventsStartLate =
            props.shouldAdjustStartTimeEndTime && getDefaultShouldShortenEventDurationStartLate();
        const shouldEventsEndEarly =
            props.shouldAdjustStartTimeEndTime && getDefaultShouldShortenEventDurationEndEarly();

        return (
            (props.isStartTimePicker && shouldEventsStartLate) ||
            (props.isEndTimePicker && shouldEventsEndEarly)
        );
    };

    /**
     * Checks if an index is a speedy meeting index.
     * i.e. if the corresponding time is a speedy time (adjusted based on the settings) or a regular time.
     * e.g. for speedy meetings-start late, start time picker options: 12:00(regular), 12:05(speedy), 12:30(regular), 12:35(speedy) etc
     * e.g. for speedy meetings-end early, end time picker options: 12:00(regular), 12:25(speedy), 12:30(regular), 12:55(speedy) etc
     * @param index The time option index
     */
    const isSpeedyMeetingIndex = (index: number): boolean => {
        return index % 2 !== 0;
    };

    /**
     * Gets the duration to shorten the events by based on speedy meetings settings.
     * @param startTime
     * @param endTime
     */
    const getAdjustedShortenEventsDurationBy = (startTime: OwaDate, endTime: OwaDate): number => {
        const interval = 60 - getDefaultEndLongEventsBy();
        const newDuration = differenceInMinutes(endTime, startTime);

        if (newDuration < interval) {
            return getDefaultEndShortEventsBy();
        }
        return getDefaultEndLongEventsBy();
    };

    /**
     * Updates the first time option for start time picker when speedy meetings - start late is enabled.
     * i.e. updates start time from 12:00 to 12:05.
     * @param startTime
     * @param endTime
     */
    const updateStartTimeWhenStartEventsLate = (startTime: OwaDate, endTime: OwaDate): OwaDate => {
        const shouldEventsStartLate =
            props.shouldAdjustStartTimeEndTime && getDefaultShouldShortenEventDurationStartLate();

        if (!shouldEventsStartLate || !props.isStartTimePicker) {
            return startTime;
        }

        const adjustedShortenEventsDurationBy = getAdjustedShortenEventsDurationBy(
            selectedTime,
            endTime
        );

        return addMinutes(startTime, adjustedShortenEventsDurationBy);
    };

    /**
     * Update the time options for the end time picker when the speedy meetings - end early setting is on
     * @param startTime
     * @param dates
     */
    const updateDates = (startTime: OwaDate, dates: OwaDate[]): OwaDate[] => {
        const shouldEventsEndEarly =
            props.shouldAdjustStartTimeEndTime && getDefaultShouldShortenEventDurationEndEarly();

        if (!shouldEventsEndEarly || !props.isEndTimePicker) {
            return dates;
        }

        const eventStartTimeInCorrectTz = owaDate(selectedTime.tz, startTime);
        const updatedDates: OwaDate[] = [];
        dates.map((date, index) => {
            if (index == 0) {
                updatedDates.push(date);
                return;
            }
            const configuredEndTime = props.shouldAdjustStartTimeEndTime
                ? getConfiguredEventEndTime(eventStartTimeInCorrectTz, date)
                : date;
            updatedDates.push(configuredEndTime);

            // For End Events Early, we want to add the original and the adjusted time options to the end time picker
            // For example: 1:25, 1:30, 1:50, 2:00, 2:20, 2:30, 2:50, 3:00 etc.
            if (isSpeedyAndRegularTimesFeatureEnabled() && !isEqual(date, configuredEndTime)) {
                updatedDates.push(date);
            }
        });

        return updatedDates;
    };

    /**
     * Generates an id for each option based on the timestamp
     * @param date
     */
    const getTimePickerOptionId = (date: OwaDate): string => {
        if (!date) {
            return '';
        }

        return 'TimePicker' + getTimestamp(date);
    };

    const {
        readOnly,
        className,
        ariaLabel,
        onBlur,
        selectedTime,
        timePickerComboBoxRootStyle,
    } = props;
    const options = createTimeOptions();
    const comboBoxStylesWithoutLowerContent: Partial<IComboBoxStyles> = {
        optionsContainerWrapper: styles.optionsContainerWrapperStyles,
        root: mergeStyles(timePickerComboBoxRootStyle, styles.timePickerComboBoxWrapper),
        input: styles.timePickerComboBoxInput,
        optionsContainer: styles.timePickerComboBoxList,
    };
    const comboBoxStylesWithLowerContent: Partial<IComboBoxStyles> = {
        optionsContainerWrapper: styles.lowerContent,
        root: mergeStyles(timePickerComboBoxRootStyle, styles.timePickerComboBoxWrapper),
        input: styles.timePickerComboBoxInput,
        optionsContainer: styles.timePickerComboBoxList,
    };
    const comboBoxStyles = props.onRenderLowerContent
        ? comboBoxStylesWithLowerContent
        : comboBoxStylesWithoutLowerContent;
    const closestTime = getClosestTime(options, selectedTime);
    const selectedOption = closestTime ? closestTime.date : selectedTime;

    const onScrollToItem = () => {
        const element = document.getElementById(getTimePickerOptionId(selectedOption));
        if (element) {
            element.scrollIntoView(true);
        }
    };

    return (
        <div
            className={classNames(
                className,
                {
                    [styles.grey]: readOnly,
                },
                styles.timePicker
            )}>
            <div className={styles.timePickerWrapper}>
                <ComboBox
                    comboBoxOptionStyles={comboBoxOptionStyle()}
                    ariaLabel={ariaLabel}
                    componentRef={comboBox.current}
                    allowFreeform={true}
                    autoComplete="on"
                    text={formatUserTime(selectedTime)}
                    options={options}
                    onChange={onChanged}
                    onScrollToItem={onScrollToItem}
                    caretDownButtonStyles={chevronStyles}
                    selectedKey={getTimestamp(selectedTime)}
                    styles={comboBoxStyles}
                    onRenderLowerContent={props.onRenderLowerContent}
                    onRenderOption={onRenderOption}
                    onBlur={onBlur}
                    disabled={readOnly}
                    onMenuDismissed={props.onMenuDismissed}
                />
            </div>
        </div>
    );
});

/**
 * Checks if either of the flights which shows both speedy times and non-speedy/regular times is enabled.
 */
function isSpeedyAndRegularTimesFeatureEnabled(): boolean {
    return (
        isFeatureEnabled('cal-mf-shortenEventsDurationTimePicker') ||
        isFeatureEnabled('cal-mf-shortenEventsDurationTimePickerLogic')
    );
}
